import type { ActionEvent, ActionIdentity, Attempt } from "./types";

/**
 * The action state machine, as a pure reducer.
 *
 * This is the whole of `MACHINE.md`'s transition matrix and nothing else — no
 * I/O, no clock, no React — so `src/tests/actionMachine.test.ts` can enumerate
 * every state x event cell without a DOM or a server.
 *
 * `null` means "no attempt", both as input (the key is idle) and as output
 * (evict this attempt). Eviction is how supersession, release and
 * acknowledgement all end: one exit, so the store has exactly one place to
 * release an attempt's observer, timers and queue slot. Cleanup can never live
 * in an event handler here, because fencing drops a superseded attempt's
 * events by design.
 */
export function reduce(
  attempt: Attempt | null,
  event: ActionEvent,
): Attempt | null {
  // TRIGGER is the only event that may arrive without a current attempt, and
  // the only one not fenced — it CREATES the identity everything else matches.
  if (event.type === "TRIGGER") {
    return {
      attemptId: event.attemptId ?? (attempt?.attemptId ?? 0) + 1,
      status: "sent",
      identity: null,
      awaitingDisplay: false,
      slow: false,
      error: null,
      startedAt: event.at,
    };
  }

  if (!attempt) return null;

  // Eviction events are unfenced on purpose: they come from the store, which
  // is acting on the key rather than on a particular attempt.
  if (event.type === "SUPERSEDED" || event.type === "RELEASED") return null;

  if (event.type === "ACKNOWLEDGE") {
    // Dismissing a still-running attempt would strand its observer and hide
    // live work; only settled attempts can be dismissed.
    return isPending(attempt) ? attempt : null;
  }

  // Everything below is server-derived and must belong to THIS attempt. A
  // client-side counter can't fence server data on its own, which is why the
  // identity gets bound on accept — but the counter is what stops a superseded
  // attempt's late callbacks from resolving the current one.
  if (event.attemptId !== attempt.attemptId) return attempt;

  switch (event.type) {
    case "SLOW":
      // Presentation only. A run we can still see is alive however long it
      // takes; elapsed time never changes what an attempt IS. Meaningless once
      // settled — a finished attempt is not "taking a while".
      if (!isPending(attempt) || attempt.slow) return attempt;
      return { ...attempt, slow: true };

    case "ACCEPTED":
      if (attempt.status !== "sent") return attempt;
      return { ...attempt, status: "applying", identity: event.identity };

    case "ACCEPTED_SETTLED":
      // Nothing was queued because the result was already current. There is no
      // run to watch, so this is done — the read model already holds it.
      if (attempt.status !== "sent") return attempt;
      return { ...attempt, status: "succeeded" };

    case "REJECTED":
      if (attempt.status !== "sent") return attempt;
      return { ...attempt, status: "failed", error: event.error };

    case "INDETERMINATE":
      // Only from `sent`. Once the server has accepted (`applying`) the write
      // is known-committed, so losing the channel is a failure to OBSERVE —
      // that is RELEASED, not unknown.
      if (attempt.status !== "sent") return attempt;
      return { ...attempt, status: "unknown", error: event.error };

    case "CONTROL_TERMINAL":
      if (attempt.status !== "applying") return attempt;
      // Success is a claim about content the user has not seen yet, so it
      // waits for the read model. Failure and cancellation are claims about
      // the action itself, which the run record already establishes in full —
      // and a failed run usually leaves no artifact for a display query to
      // confirm, so gating them here would hide the error forever.
      if (event.terminal === "SUCCEEDED") {
        return attempt.awaitingDisplay
          ? attempt
          : { ...attempt, awaitingDisplay: true };
      }
      return {
        ...attempt,
        status: event.terminal === "FAILED" ? "failed" : "canceled",
        error: event.error ?? attempt.error,
      };

    case "DISPLAY_CONFIRMED":
      if (attempt.status !== "applying") return attempt;
      return { ...attempt, status: "succeeded", awaitingDisplay: false };

    case "EVIDENCE_APPLIED":
      // The only way out of `unknown` that isn't a user decision. Note there
      // is deliberately no absent-read transition: "we looked and it wasn't
      // there" is not "it didn't happen".
      if (attempt.status !== "unknown") return attempt;
      return { ...attempt, status: "succeeded", error: null };
  }
}

function isPending(attempt: Attempt): boolean {
  return attempt.status === "sent" || attempt.status === "applying";
}

/** Convenience for tests and adoption: build an attempt already in flight. */
export function adoptedAttempt(
  attemptId: number,
  identity: ActionIdentity,
  startedAt: number,
): Attempt {
  return {
    attemptId,
    status: "applying",
    identity,
    awaitingDisplay: false,
    slow: false,
    error: null,
    startedAt,
  };
}
